import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isEqual, isNumber } from 'lodash';
import { IFiltersSliceState } from '../../filters/filtersSlice';
import { RootState } from '../../store';
import { getaTrendData } from './trendComponentAPI';

export interface ITrendComponentAPIResponse {
  values: IData[]
  avannis: string
  stat: string
  qid: string
  q: string
  label: string
  small_text: string
  statistic: string
  segmentName: string
  segmentCategory: string
  surveyLabel: string
  factor: number
}

interface ITrendComponentState {
  isLoading: boolean
  hasError: boolean
  apiResponse: ITrendComponentAPIResponse
  chartHTMLSubtitle: string
  chartDataPoints: IChartDataPoint[]
  chartCategories: string[]
  firstLoadFulfilled: boolean
  statisticLabel: string
}

interface IData {
  survey: string
  abbr: string
  display: string
  overall: number
  surveys: number
  im: number
  net: number
  isHidden: number
  precision: number
}

interface IChartDataPoint {
  y: number
  interviews: number
  dataLabels?: IDataLabel
}

interface IDataLabel {
  enabled: boolean
  format: string
}

const initialState: ITrendComponentState = {
  isLoading: false,
  hasError: false,
  apiResponse: {
    values: [],
    avannis: '',
    stat: '',
    qid: '',
    q: '',
    label: '',
    small_text: '',
    statistic: '',
    segmentName: '',
    segmentCategory: '',
    surveyLabel: '',
    factor: 0,
  },
  chartHTMLSubtitle: '',
  chartCategories: [],
  chartDataPoints: [],
  firstLoadFulfilled: false,
  statisticLabel: '',
};

export const loadTrendData = createAsyncThunk(
  'trendComponent/get',
  async (filters: IFiltersSliceState) => {
    const response = await getaTrendData(filters);
    return response;
  })

export const trendComponentSlice = createSlice({
  name: 'trendComponent',
  initialState,
  reducers: {
    updateTrendComponentState: (state: ITrendComponentState, action) => {
      const apiResponse = action.payload
      if (!isEqual(state, apiResponse)) {
        state.apiResponse = apiResponse;
      }
      return state;
    },
    setHTMLSubtitle: (state: ITrendComponentState) => {
      const apiResponse = state.apiResponse;
      const smallText = apiResponse.small_text;
      const statistic = apiResponse.statistic;
      const surveyLabel = apiResponse.surveyLabel;
      const segmentLabel = apiResponse.segmentName;
      const segmentCategory = apiResponse.segmentCategory;

      const measure = isNumber(statistic) ? `Rating of ${statistic}` : statistic;

      let draftHtmlSubtitle = `<small class="center-align">${smallText}: ${measure} | ${surveyLabel}`

      if (segmentLabel !== '') {
        draftHtmlSubtitle += ` | ${segmentCategory}: ${segmentLabel}`
      }
      draftHtmlSubtitle += `</small>`

      state.chartHTMLSubtitle = draftHtmlSubtitle;

      // return state;
    },
    setChartData: (state) => {
      const categories: string[] = [];
      const values: IChartDataPoint[] = [];
      const suffix = (state.apiResponse.factor.toString() === '100') ? '%' : ''

      state.apiResponse.values.forEach((value) => {
        categories.push(value.display);
        values.push({
          y: Number(value.overall),
          interviews: Number(value.surveys)
        });
      });

      if (values.length > 1) {
        values[values.length - 1].dataLabels = {
          enabled: true,
          format: '{y}' + suffix
        }
      }

      state.chartDataPoints = values;
      state.chartCategories = categories;

      // return state;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadTrendData.pending, (state) => {
      state.isLoading = true;
      state.hasError = false;
    })
    builder.addCase(loadTrendData.fulfilled, (state, action) => {
      if (action.payload) {
        state.apiResponse = action.payload;
        const apiResponse = state.apiResponse;
        state.isLoading = false;
        state.hasError = false;
        state.statisticLabel = apiResponse.statistic.length ? `${apiResponse.statistic} ` : '';
        if (!state.firstLoadFulfilled) {
          state.firstLoadFulfilled = true;
        }
      }
    })
    builder.addCase(loadTrendData.rejected, (state, action) => {
      state.isLoading = false;
      if (action.error?.name !== 'AbortError') {
        state.hasError = true;
      }
    })
  }
});

export const selectTrendComponentState = (state: RootState) => state.trendComponent;
export const selectTrendAPIResponse = (state: RootState) => state.trendComponent.apiResponse;
export const selectIsTrendDataLoading = (state: RootState) => state.trendComponent.isLoading;
export const selectTrendHasError = (state: RootState) => state.trendComponent.hasError;
export const selectFirstLoadFulfilled = (state: RootState) => state.trendComponent.firstLoadFulfilled;
export const selectTrendLabel = (state: RootState) => state.trendComponent.apiResponse.label;
export const selectTrendChartData = (state: RootState) => state.trendComponent.chartDataPoints;
export const selectTrendChartCategories = (state: RootState) => state.trendComponent.chartCategories;
export const { setHTMLSubtitle, setChartData } = trendComponentSlice.actions;
export default trendComponentSlice.reducer;
